import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import Container from '../components/container';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faComments,
  faServer,
  faCode,
  faArrowRight,
  faCheck,
  faChevronDown,
  faChevronUp
} from '@fortawesome/free-solid-svg-icons';

/**
 * ServicesSection with improved headline readability
 */
const ServicesSection = ({ openContactModal }) => {
  const { t } = useTranslation('home');
  const [expandedCard, setExpandedCard] = useState(null);
  
  // Icons for each service
  const serviceIcons = [
    faComments, // Professional consulting
    faServer,   // Hosting
    faCode      // Development
  ];
  
  // Toggle card expansion
  const toggleExpand = (index) => {
    if (expandedCard === index) {
      setExpandedCard(null);
    } else {
      setExpandedCard(index);
    }
  };
  
  // Action handlers for buttons
  const handleButtonClick = (template) => {
    switch (template) {
      case 'appointment':
      case 'Termin':
        window.location.href = 'https://drive.pathconnect.de/apps/appointments/pub/eMgHf4y8FMdP5g%3D%3D/form';
        break;
      default:
        return null;
    }
  };

  return (
    <section className="relative pb-16">
      {/* Very subtle gradient overlay to soften the transition */}
      <div className="absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-transparent to-gray-950/20 z-0"></div>
      
      {/* Content with proper spacing */}
      <Container className="relative z-10 mt-8 md:mt-10">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {t('c2', { returnObjects: true }).map((service, index) => (
            <div 
              key={index} 
              className="group relative bg-gradient-to-br from-gray-800/40 to-gray-900/40 backdrop-blur-sm rounded-xl border border-gray-700/50 overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:border-primary/50 hover:shadow-lg hover:shadow-primary/10"
            >
              {/* Top accent line with animation */}
              <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-primary to-blue-500 transform origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-500"></div>
              
              {/* Background subtle glow on hover */}
              <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-blue-600/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              
              {/* Large icon watermark in background */}
              <div className="absolute -right-6 -top-6 opacity-5 group-hover:opacity-10 transition-opacity duration-500">
                <FontAwesomeIcon 
                  icon={serviceIcons[index]} 
                  className="text-primary text-7xl transform -rotate-12"
                />
              </div>
              
              <div className="p-6 relative z-10">
                {/* Improved title with better readability */}
                <h3 className="text-xl font-bold mb-4 relative">
                  {/* Changed from gradient to solid color for better readability */}
                  <span className="text-primary group-hover:text-primary transition-colors duration-300">
                    {service.title}
                  </span>
                  {/* Accent line below title */}
                  <span className="block h-0.5 w-12 bg-gradient-to-r from-primary to-transparent mt-2 group-hover:w-20 transition-all duration-300"></span>
                </h3>
                
                {/* Show either highlights by default or full description when expanded */}
                {expandedCard !== index ? (
                  <ul className="mt-6 mb-6 space-y-3">
                    {service.highlights.map((highlight, idx) => (
                      <li key={idx} className="flex items-start text-gray-300 group/item">
                        <span className="flex-shrink-0 w-5 h-5 mt-0.5 mr-3 rounded-full bg-gray-800/80 border border-primary/30 flex items-center justify-center group-hover/item:border-primary/50 transition-all duration-300">
                          <FontAwesomeIcon icon={faCheck} className="text-primary text-xs" />
                        </span>
                        <span className="group-hover/item:text-gray-200 transition-colors duration-300">
                          {highlight}
                        </span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-gray-300 text-sm mt-4 mb-6 leading-relaxed">
                    {service.description}
                  </p>
                )}
                
                {/* Toggle button */}
                <button 
                  onClick={() => toggleExpand(index)}
                  className="inline-flex items-center text-primary text-sm mb-4 hover:text-primary/80 transition-colors duration-300"
                >
                  {expandedCard === index ? 'Weniger anzeigen' : 'Mehr erfahren'}
                  <FontAwesomeIcon 
                    icon={expandedCard === index ? faChevronUp : faChevronDown} 
                    className="ml-1 text-xs"
                  />
                </button>
                
                {/* Button - with hover effects */}
                <div className="mt-auto">
                  {service.link ? (
                    <Link 
                      to={service.link === "/kontakt" ? "/contact" : service.link}
                      className="group/btn relative overflow-hidden inline-flex items-center py-2 px-4 bg-gray-800/80 border border-primary/50 text-primary rounded-lg transition-all duration-300 hover:bg-primary/10"
                    >
                      <span className="relative text-sm">{service.button}</span>
                      <FontAwesomeIcon 
                        icon={faArrowRight} 
                        className="relative ml-2 opacity-80 transform group-hover/btn:translate-x-1 transition-all duration-300"
                      />
                    </Link>
                  ) : (
                    <button
                      onClick={() => handleButtonClick(service.template)}
                      className="group/btn relative overflow-hidden inline-flex items-center py-2 px-4 bg-gray-800/80 border border-primary/50 text-primary rounded-lg transition-all duration-300 hover:bg-primary/10"
                    >
                      <span className="relative text-sm">{service.button}</span>
                      <FontAwesomeIcon 
                        icon={faArrowRight} 
                        className="relative ml-2 opacity-80 transform group-hover/btn:translate-x-1 transition-all duration-300"
                      />
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </Container>
    </section>
  );
};

export default ServicesSection;